import React from "react";
import { Grid } from "@mui/material";
import {
  ArrayInput,
  AutocompleteInput,
  BooleanInput,
  maxLength,
  NumberInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleFormIterator,
  TextInput,
} from "react-admin";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import { administrationRequests } from "api/administrationApi";
import { graduationRequests } from "api/graduationApi";
import { settings } from "app";
import { useWatch } from "react-hook-form";

const SaveForm = () => {
  const isMonthlyInternship = useWatch({ name: "isMonthlyInternship" });
  const isWards = useWatch({ name: "isWards" });

  return (
    <>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          md={4}
        >
          <TextInput
            source="name"
            label="Име"
            validate={maxLength(100)}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <CustomDateInput
            source="entryDate"
            label="Дата на входиране"
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <ReferenceInput
            source="specialityId"
            reference={administrationRequests.getSpecialitiesSelect()}
          >
            <AutocompleteInput
              label="Специалност"
              validate={required()}
              filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
            />
          </ReferenceInput>
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <NumberInput
            source="credits"
            label="Кредити"
            step="0.01"
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", alignItems: "center" }}
        >
          <BooleanInput
            source="isMonthlyInternship"
            label="Месечен стаж?"
          />

          <BooleanInput
            source="isWards"
            label="Болнични отделения?"
          />
        </Grid>
      </Grid>
      {!isMonthlyInternship && !isWards && (
        <Grid
          container
          spacing={2}
        >
          <ArrayInput
            source="disciplines"
            label="Дисциплини"
            defaultValue={[{}]}
            sx={{ ml: 2 }}
          >
            <SimpleFormIterator
              disableReordering
              disableRemove
              disableClear
              sx={settings.style.iteratorWithoutGap}
            >
              <Grid
                container
                spacing={2}
                alignItems="center"
              >
                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  <ReferenceInput
                    source="disciplineId"
                    reference={administrationRequests.getDisciplinesSelect()}
                  >
                    <AutocompleteInput
                      label="Дисциплина"
                      validate={required()}
                      filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                    />
                  </ReferenceInput>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  <ReferenceInput
                    source="examType"
                    reference={graduationRequests.getPlanExamTypesSelect()}
                  >
                    <SelectInput
                      label="Тип изпит"
                      optionText="nameBg"
                      validate={required()}
                      emptyText={true}
                    />
                  </ReferenceInput>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  <ReferenceInput
                    source="scheduleType"
                    reference={graduationRequests.getPlanScheduleTypesSelect()}
                  >
                    <SelectInput
                      label="Тип разписание"
                      optionText="nameBg"
                      validate={required()}
                      emptyText={true}
                    />
                  </ReferenceInput>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  <BooleanInput
                    source="hasPresentation"
                    label="Има ли презентация?"
                  />
                </Grid>
              </Grid>
              <ArrayInput
                source="requirements"
                label="Изисквания"
                defaultValue={[{}]}
              >
                <SimpleFormIterator
                  disableReordering
                  disableClear
                  sx={settings.style.iteratorWithoutGap}
                >
                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid
                      item
                      xs={12}
                      md={6}
                    >
                      <TextInput
                        source="requirementNameBg"
                        label="Норматив (български)"
                        validate={[required(), maxLength(200)]}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={6}
                    >
                      <TextInput
                        source="requirementNameEn"
                        label="Норматив (английски)"
                        validate={[required(), maxLength(200)]}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={6}
                    >
                      <TextInput
                        source="unitNameBg"
                        label="Мерна единица (български)"
                        validate={[required(), maxLength(50)]}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={6}
                    >
                      <TextInput
                        source="unitNameEn"
                        label="Мерна единица (английски)"
                        validate={[required(), maxLength(50)]}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={6}
                    >
                      <NumberInput
                        source="requiredCount"
                        label="Необходим брой"
                      />
                    </Grid>
                  </Grid>
                </SimpleFormIterator>
              </ArrayInput>
            </SimpleFormIterator>
          </ArrayInput>
        </Grid>
      )}
      {isWards && (
        <Grid
          container
          spacing={2}
          xs={12}
          md={6}
        >
          <ArrayInput
            source="wards"
            label="Болнични отделения"
            defaultValue={[{}]}
            sx={{ ml: 2 }}
          >
            <SimpleFormIterator
              disableReordering
              disableClear
              inline
              sx={settings.style.iteratorWithoutGap}
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <ReferenceInput
                  source="wardId"
                  reference={administrationRequests.getWardsSelect()}
                >
                  <AutocompleteInput
                    optionValue="id"
                    optionText="name"
                    label="Болнични отделения"
                    filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                  />
                </ReferenceInput>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <NumberInput
                  source="horariumHours"
                  label="Хорариум часове"
                />
              </Grid>
            </SimpleFormIterator>
          </ArrayInput>
        </Grid>
      )}
    </>
  );
};

export default SaveForm;
